import React, {useCallback, useEffect, useState} from "react";
import {
    addSiteStaff, assignStaffProject, getSiteStaff, removeStaffAuthenticator, setUpStaffAuthenticator, suspendSiteStaff,
    unassignStaffProject,
} from "../api/siteStaff";
import AuthenticatorSetup from "../components/AuthenticatorSetup";

const STATUS = {
    PENDING: {label: "Waiting for OGM approval", tone: "amber"},
    ACTIVE: {label: "Active", tone: "green"},
    SUSPENDED: {label: "Suspended", tone: "grey"},
};
const EMPTY = {name: "", mobile: "", email: ""};

const since = (iso) => (iso ? new Date(iso).toLocaleTimeString([], {hour: "numeric", minute: "2-digit"}) : "");

/**
 * Builders' own sales staff for live tours (review answer C2: tours of a builder's project ring only that builder's
 * staff). A new person waits for OGM approval, then signs in to the OGM Agent app with their mobile and checks in at
 * the projects assigned here.
 */
const SiteStaffTab = ({projects, approved}) => {
    const [staff, setStaff] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [adding, setAdding] = useState(false);
    const [busy, setBusy] = useState(null);
    const [confirming, setConfirming] = useState(null);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [showing, setShowing] = useState(null);

    const load = useCallback(async () => {
        try {
            setStaff(await getSiteStaff());
        } catch (err) {
            setError(err.message);
            setStaff([]);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const run = async (key, fn) => {
        setBusy(key);
        setError("");
        setNotice("");
        try {
            await fn();
            await load();
            return true;
        } catch (err) {
            setError(err.message);
            return false;
        } finally {
            setBusy(null);
            setConfirming(null);
        }
    };

    const setUpQr = (s) => run(`qr-${s.agentId}`, async () => {
        setShowing({name: s.name, setup: await setUpStaffAuthenticator(s.agentId)});
    });

    const submit = async (e) => {
        e.preventDefault();
        const mobile = form.mobile.replace(/\D/g, "");
        if (!form.name.trim()) return setError("Please enter their name.");
        if (!/^[6-9]\d{9}$/.test(mobile)) return setError("Please enter a valid 10-digit mobile number.");
        const ok = await run("add", () => addSiteStaff({name: form.name.trim(), mobile, email: form.email.trim() || null}));
        if (ok) {
            setForm(EMPTY);
            setAdding(false);
            setNotice(`${form.name.trim()} is added. OGM will approve them shortly; then they can sign in to the OGM Agent app with ${mobile}.`);
        }
    };

    if (!approved) {
        return <div className="brk-card"><p className="brk-empty">Site staff can be added once your account is approved.</p></div>;
    }

    const liveProjects = projects.filter((p) => p.reviewStatus === "APPROVED");

    return (
        <div className="brk-card">
            <div className="brk-card__head">
                <p className="brk-card__lead">
                    Your sales staff at the site take buyers' live video tours of your projects in the <b>OGM Agent</b> app.
                    Add them here, choose their projects, and they check in on the app when they're at the site.
                </p>
                {!adding && (
                    <button type="button" className="brk-btn brk-btn--primary brk-btn--sm" onClick={() => setAdding(true)}>+ Add staff</button>
                )}
            </div>
            {error && <div className="brk-notice brk-notice--error" role="alert">{error}</div>}
            {notice && <div className="brk-notice brk-notice--ok" role="status">{notice}</div>}

            {showing && <AuthenticatorSetup name={showing.name} setup={showing.setup} onDone={() => setShowing(null)}/>}

            {adding && (
                <form className="brk-grid" onSubmit={submit} style={{marginBottom: 20}}>
                    <label className="brk-field">
                        <span className="brk-field__label">Name</span>
                        <input className="brk-input" value={form.name} maxLength={120} autoFocus
                               onChange={(e) => setForm((f) => ({...f, name: e.target.value}))}/>
                    </label>
                    <label className="brk-field">
                        <span className="brk-field__label">Mobile (they sign in with this)</span>
                        <input className="brk-input" inputMode="numeric" value={form.mobile} placeholder="10-digit mobile"
                               onChange={(e) => setForm((f) => ({...f, mobile: e.target.value.replace(/\D/g, "").slice(0, 10)}))}/>
                    </label>
                    <label className="brk-field">
                        <span className="brk-field__label">Email (optional)</span>
                        <input className="brk-input" type="email" value={form.email}
                               onChange={(e) => setForm((f) => ({...f, email: e.target.value}))}/>
                    </label>
                    <div className="brk-actions brk-field--wide">
                        <button type="submit" className="brk-btn brk-btn--primary brk-btn--sm" disabled={busy === "add"}>
                            {busy === "add" ? "Adding…" : "Add"}
                        </button>
                        <button type="button" className="brk-btn brk-btn--ghost brk-btn--sm"
                                onClick={() => { setAdding(false); setForm(EMPTY); setError(""); }}>Cancel</button>
                    </div>
                </form>
            )}

            {!staff ? <p className="brk-empty">Loading…</p> : staff.length === 0 ? (
                <p className="brk-empty">No site staff yet. Add the people who show buyers round your projects.</p>
            ) : (
                <div className="brk-table-wrap">
                    <table className="brk-table">
                        <thead>
                        <tr><th>Name</th><th>Status</th><th>Right now</th><th>Projects</th><th/></tr>
                        </thead>
                        <tbody>
                        {staff.map((s) => {
                            const st = STATUS[s.status] || {label: s.status, tone: "grey"};
                            const assignedIds = new Set(s.sites.filter((x) => x.siteType === "PROJECT").map((x) => x.siteId));
                            const addable = liveProjects.filter((p) => !assignedIds.has(p.id));
                            const suspended = s.status === "SUSPENDED";
                            return (
                                <tr key={s.agentId}>
                                    <td>
                                        <div className="brk-strong">{s.name}</div>
                                        <div className="brk-hint">{(s.mobile || "").replace(/^91/, "")}{s.email ? ` · ${s.email}` : ""}</div>
                                    </td>
                                    <td>
                                        <span className={`brk-badge brk-badge--${st.tone}`}>{st.label}</span>
                                        {!suspended && (
                                            <div className="brk-hint" style={{marginTop: 4}}>
                                                {s.hasAuthenticator ? "Authenticator set up · " : ""}
                                                <button type="button" className="brk-link" disabled={busy === `qr-${s.agentId}`}
                                                        onClick={() => setUpQr(s)}>
                                                    {s.hasAuthenticator ? "New QR code" : "Set up authenticator"}
                                                </button>
                                                {s.hasAuthenticator && (
                                                    <> · <button type="button" className="brk-link brk-link--danger" disabled={busy === `qr-${s.agentId}`}
                                                                 onClick={() => run(`qr-${s.agentId}`, () => removeStaffAuthenticator(s.agentId))}>
                                                        Turn off
                                                    </button></>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        {s.checkin && !s.checkin.checkedOutAt ? (
                                            <span className="brk-badge brk-badge--green">
                                                At {s.checkin.siteName || "site"} since {since(s.checkin.checkedInAt)}{s.busy ? " · on a tour" : ""}
                                            </span>
                                        ) : <span className="brk-hint">Not checked in</span>}
                                    </td>
                                    <td>
                                        <div className="brk-chips">
                                            {s.sites.map((x) => (
                                                <span key={x.assignmentId ?? `${x.siteType}-${x.siteId}`} className="brk-chip brk-chip--on">
                                                    {x.name || `${x.siteType.toLowerCase()} ${x.siteId}`}
                                                    {!suspended && x.assignmentId && (
                                                        <button type="button" className="brk-link" aria-label={`Remove ${x.name} from ${s.name}`}
                                                                disabled={busy === `un-${x.assignmentId}`}
                                                                onClick={() => run(`un-${x.assignmentId}`, () => unassignStaffProject(s.agentId, x.assignmentId))}>
                                                            ✕
                                                        </button>
                                                    )}
                                                </span>
                                            ))}
                                            {!suspended && addable.length > 0 && (
                                                <select className="brk-input brk-input--sm" value="" disabled={busy === `as-${s.agentId}`}
                                                        aria-label={`Add a project for ${s.name}`}
                                                        onChange={(e) => e.target.value && run(`as-${s.agentId}`,
                                                            () => assignStaffProject(s.agentId, Number(e.target.value)))}>
                                                    <option value="">+ Add project</option>
                                                    {addable.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                                                </select>
                                            )}
                                        </div>
                                        {!suspended && s.sites.length === 0 && (
                                            <div className="brk-hint">
                                                {liveProjects.length ? "No project yet: tours won't ring them." : "Your projects can be assigned once they're approved."}
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        {suspended ? null : confirming === s.agentId ? (
                                            <span className="brk-row-actions">
                                                <span className="brk-hint">Remove access?</span>
                                                <button type="button" className="brk-link brk-link--danger" disabled={busy === `rm-${s.agentId}`}
                                                        onClick={() => run(`rm-${s.agentId}`, () => suspendSiteStaff(s.agentId))}>Yes</button>
                                                <button type="button" className="brk-link" onClick={() => setConfirming(null)}>No</button>
                                            </span>
                                        ) : (
                                            <button type="button" className="brk-link brk-link--danger" onClick={() => setConfirming(s.agentId)}>
                                                Remove access
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </div>
            )}
            <p className="brk-hint" style={{marginTop: 14}}>
                Removing access signs them out of the OGM Agent app straight away. Their past tours and leads stay on record.
            </p>
        </div>
    );
};

export default SiteStaffTab;
